import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime, filter, map, startWith } from 'rxjs/operators';
import { SafeArea } from 'capacitor-plugin-safe-area';

@Injectable({
    providedIn: 'root',
})
export class CalcCssVarsService {
    public w = window.innerWidth;
    public h = window.innerHeight;
    public isMobileView$ = new BehaviorSubject<boolean>(this.w < 768);
    public isNotDesktopView$ = new BehaviorSubject<boolean>(this.w < 1280);
    public calcChanges$ = new BehaviorSubject<{ w: number; h: number } | null>(null);

    private lastW = this.w;
    private lastH = this.h;
    private keyboardThreshold = 100;

    public initialize(): void {
        SafeArea.addListener('safeAreaChanged', (data) => {
            const { insets } = data;
            console.log('SafeArea safeAreaChanged ', data);
        }).then();
        fromEvent(window, 'resize')
            .pipe(
                debounceTime(100),
                map(() => ({
                    width: window.innerWidth,
                    height: window.innerHeight,
                })),
                startWith({
                    width: window.innerWidth,
                    height: window.innerHeight,
                }),
                filter(({ width, height }) => {
                    const widthChanged = width !== this.lastW;
                    const prevHeight = this.lastH;
                    const heightDropped =
                        this.lastH - height > this.keyboardThreshold || Math.abs(this.lastH - height) > this.keyboardThreshold;
                    const isKeyboardResize = !widthChanged && heightDropped;
                    this.lastW = width;
                    this.lastH = height;

                    return prevHeight === height || (Math.abs(prevHeight - height) > this.keyboardThreshold && !isKeyboardResize);
                }),
            )
            .subscribe(({ width, height }) => {
                this.isNotDesktopView$.next(width < 1280);
                this.isMobileView$.next(width < 768);
                this.fnSet(width, height);
            });
    }

    public reinitialize(): void {
        this.fnSet(window.innerWidth, window.innerHeight);
    }

    public fnSet(windowWidthInPixels: number, windowHeightInPixels: number) {
        const zoom = document.documentElement.style.getPropertyValue('--zoom')
            ? Number(document.documentElement.style.getPropertyValue('--zoom'))
            : 1;
        const vw = windowWidthInPixels / 100;
        const vh = windowHeightInPixels / 100;
        const px = windowWidthInPixels >= 1280 ? 1 * zoom : (vh + vw) * (windowWidthInPixels >= 768 ? 0.029 : 0.08035) * zoom;

        document.documentElement.style.setProperty('--vw', `${vw}px`);
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        document.documentElement.style.setProperty('--1px', `${px}px`);
        document.documentElement.style.setProperty('--1pxFont', `${px}px`);

        SafeArea.getSafeAreaInsets().then((data) => {
            const { insets } = data;
            document.body.style.setProperty('--ion-safe-area-top', `${insets.top}px`);
            document.body.style.setProperty('--ion-safe-area-right', `${insets.right}px`);
            document.body.style.setProperty('--ion-safe-area-bottom', `${insets.bottom}px`);
            document.body.style.setProperty('--ion-safe-area-left', `${insets.left}px`);
        });

        this.calcChanges$.next({ w: windowWidthInPixels, h: windowHeightInPixels });
    }
}
